import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  Briefcase,
  Code,
  Database,
  Mail,
  MapPin,
  Phone,
  Lightbulb,
  Target,
  Users,
  TrendingUp,
  Shield,
  Book,
  MessageSquare,
  Star,
  ChevronRight,
  Github,
  Linkedin,
  ExternalLink,
  Folder,
  Image
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SkillIcon from '../components/SkillIcon';
import StatCard from '../components/StatCard';
import AccomplishmentChart from '../components/AccomplishmentChart';
import AccordionTraining from '../components/AccordionTraining';
import {
  personalInfo,
  skillsSummary,
  skills,
  careerDevelopment,
  technicalLeadership,
  extendedRoles,
  featuredInnovation,
  innovations,
  starStories,
  accomplishments,
  operationalExcellence,
  certifications,
  training,
  shoutouts,
  performanceReview
} from '../mockData';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.pageYOffset >= sectionTop - 200) {
          current = section.getAttribute('id');
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white relative">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(59, 130, 246) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <span className="text-xl font-bold text-blue-500">{personalInfo.name}</span>
            </div>
            <div className="hidden md:flex space-x-1">
              {['hero', 'career', 'skills', 'leadership', 'innovations', 'accomplishments', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeSection === section
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-4 relative bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Profile Picture */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center mb-8"
            >
              <div className="relative">
                <div className="w-40 h-40 rounded-full border-4 border-blue-500 overflow-hidden bg-slate-700 flex items-center justify-center">
                  <img 
                    src="/profile-picture.jpg" 
                    alt="Narek Onanyan" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-6xl text-gray-400">👤</div>';
                    }}
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center border-4 border-slate-900">
                  <Award className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              {personalInfo.name}
            </h1>
            <p className="text-2xl md:text-3xl text-blue-400 mb-6">
              {personalInfo.title}
            </p>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {personalInfo.tagline}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <div className="flex items-center text-gray-300">
                <MapPin className="w-5 h-5 mr-2" />
                {personalInfo.location}
              </div>
              <div className="flex items-center text-gray-300">
                <Mail className="w-5 h-5 mr-2" />
                {personalInfo.email}
              </div>
              <div className="flex items-center text-gray-300">
                <Phone className="w-5 h-5 mr-2" />
                {personalInfo.phone}
              </div>
            </div>
            <div className="flex justify-center gap-4">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Mail className="w-4 h-4 mr-2" />
                Contact Me
              </Button>
              <Button variant="outline" className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white">
                <Github className="w-4 h-4 mr-2" />
                View GitHub
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Career Development */}
      <section id="career" className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center mb-8">
              <Briefcase className="w-8 h-8 text-blue-500 mr-3" />
              <h2 className="text-4xl font-bold">Career Development</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-slate-800 border-slate-700 p-6">
                <h3 className="text-xl font-semibold mb-4 text-blue-400">Current Role</h3>
                <p className="text-gray-300 leading-relaxed">{careerDevelopment.currentRole}</p>
              </Card>
              <Card className="bg-slate-800 border-slate-700 p-6">
                <h3 className="text-xl font-semibold mb-4 text-blue-400">Career Growth Focus</h3>
                <p className="text-gray-300 leading-relaxed">{careerDevelopment.careerGrowth}</p>
              </Card>
            </div>
            <div className="mt-8">
              <h3 className="text-2xl font-semibold mb-6">Active Certifications</h3>
              <div className="flex justify-center gap-6 flex-wrap">
                {careerDevelopment.certifications.map((cert, index) => (
                  <motion.a
                    key={index}
                    href={cert.credlyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="block w-full sm:w-80"
                  >
                    <Card className="bg-slate-800 border-slate-700 p-6 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20 transition-all cursor-pointer h-full">
                      <div className="flex flex-col items-center text-center">
                        <Award className="w-12 h-12 text-blue-500 mb-3" />
                        <h4 className="font-semibold text-base mb-2 text-white">{cert.name}</h4>
                        <p className="text-sm text-gray-400 mb-3">{cert.date}</p>
                        <Badge className="mb-3 bg-green-600 text-white">{cert.status}</Badge>
                        <div className="flex items-center text-blue-400 text-sm">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Verify on Credly
                        </div>
                      </div>
                    </Card>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills" className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center mb-8">
              <Code className="w-8 h-8 text-blue-500 mr-3" />
              <h2 className="text-4xl font-bold">Technical Skills</h2>
            </div>
            
            {/* Skills Summary */}
            <p className="text-gray-300 text-lg leading-relaxed mb-12 max-w-5xl mx-auto text-center">
              {skillsSummary}
            </p>

            {/* Core Technical Skills - Row 1 */}
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-blue-400 mb-6 text-center">Core Technical Skills</h3>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-6 max-w-6xl mx-auto">
                {skills.coreSkills.map((skill, index) => (
                  <SkillIcon key={index} skill={skill} index={index} />
                ))}
              </div>
            </div>

            {/* Practical & Specialized Skills - Row 2 */}
            <div>
              <h3 className="text-2xl font-semibold text-blue-400 mb-6 text-center">Practical & Specialized Skills</h3>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-6 max-w-6xl mx-auto">
                {skills.practicalSkills.map((skill, index) => (
                  <SkillIcon key={index} skill={skill} index={index + 8} />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technical Leadership */}
      <section id="leadership" className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Users className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Professional Development & Technical Leadership</h2>
          </div>
          <Card className="bg-slate-800 border-slate-700 p-6 mb-8">
            <p className="text-lg text-gray-300 mb-4">{technicalLeadership.tenure}</p>
            <h3 className="text-xl font-semibold mb-4 text-blue-400">Key Achievements</h3>
            <ul className="space-y-3">
              {technicalLeadership.achievements.map((achievement, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start"
                >
                  <ChevronRight className="w-5 h-5 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">{achievement}</span>
                </motion.li>
              ))}
            </ul>
          </Card>

          <h3 className="text-2xl font-semibold mb-6">Extended Roles & Regional Leadership</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {extendedRoles.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6 h-full hover:border-blue-500 transition-all">
                  <h4 className="text-lg font-semibold mb-2 text-blue-400">{role.title}</h4>
                  <p className="text-gray-300 mb-3 text-sm">{role.description}</p>
                  <div className="bg-slate-900 rounded p-3">
                    <p className="text-sm text-green-400 font-medium">Impact: {role.impact}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovations */}
      <section id="innovations" className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Lightbulb className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Invent and Simplify</h2>
          </div>
          
          {/* Featured Innovation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-blue-500 p-8">
              <div className="flex items-start gap-4 mb-4">
                <Lightbulb className="w-12 h-12 text-blue-400 flex-shrink-0" />
                <div>
                  <h3 className="text-2xl font-bold mb-2 text-blue-400">{featuredInnovation.title}</h3>
                  <p className="text-gray-300 text-base leading-relaxed">{featuredInnovation.description}</p>
                </div>
              </div>
              
              {/* Demo Screenshots */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {featuredInnovation.images.map((img, i) => (
                  <div key={i} className="aspect-video bg-slate-700 rounded-lg overflow-hidden border-2 border-slate-600">
                    <img 
                      src={img} 
                      alt={`Demo screenshot ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  </div>
                ))}
              </div>
              
              {/* Features */}
              <div className="mb-4">
                <h4 className="text-lg font-semibold mb-3 text-blue-400">Key Features</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {featuredInnovation.features.map((feature, i) => (
                    <div key={i} className="flex items-start">
                      <ChevronRight className="w-4 h-4 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Technologies */}
              <div className="flex flex-wrap gap-2 mb-4">
                {featuredInnovation.technologies.map((tech, i) => (
                  <Badge key={i} variant="secondary" className="bg-blue-900 text-blue-200">
                    {tech}
                  </Badge>
                ))}
              </div>
              
              {/* Impact */}
              <div className="bg-slate-900 rounded-lg p-4 border-l-4 border-green-500">
                <p className="text-green-400 font-semibold">{featuredInnovation.impact}</p>
              </div>
            </Card>
          </motion.div>

          {/* Other Innovations */}
          <h3 className="text-2xl font-semibold mb-6">Additional Innovations</h3>
          <div className="grid lg:grid-cols-3 gap-6">
            {innovations.map((innovation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6 h-full hover:border-blue-500 transition-all">
                  <Lightbulb className="w-10 h-10 text-blue-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-3 text-white">{innovation.title}</h3>
                  <p className="text-gray-300 mb-4 text-sm">{innovation.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {innovation.technologies.map((tech, i) => (
                      <Badge key={i} variant="secondary" className="bg-slate-700">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  <div className="bg-slate-900 rounded p-3">
                    <p className="text-sm text-green-400 font-medium">{innovation.impact}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STAR Stories */}
      <section className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Target className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Customer Obsession, Bias for Action, Dive Deep</h2>
          </div>
          <div className="space-y-6">
            {starStories.map((story, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6">
                  <h3 className="text-xl font-semibold mb-4 text-blue-400">{story.principle}</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-sm text-gray-400 mb-1">SITUATION</h4>
                      <p className="text-gray-300">{story.situation}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-gray-400 mb-1">TASK</h4>
                      <p className="text-gray-300">{story.task}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-gray-400 mb-1">ACTION</h4>
                      <p className="text-gray-300">{story.action}</p>
                    </div>
                    <div className="bg-slate-900 rounded p-4">
                      <h4 className="font-semibold text-sm text-green-400 mb-1">RESULT</h4>
                      <p className="text-gray-300">{story.result}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Year-to-Date Accomplishments */}
      <section id="accomplishments" className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <TrendingUp className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Year-to-Date Accomplishments</h2>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {accomplishments.metrics.map((stat, index) => (
              <StatCard key={index} stat={stat} index={index} />
            ))}
          </div>

          {/* Chart - Hidden per user request */}
          {accomplishments.showChart && (
            <Card className="bg-slate-800 border-slate-700 p-6 mb-12">
              <h3 className="text-xl font-semibold mb-6">2024 Progress Timeline</h3>
              <AccomplishmentChart />
            </Card>
          )}

          {/* Executive Summary */}
          <Card className="bg-slate-800 border-slate-700 p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-blue-400">Executive Summary</h3>
            <ul className="space-y-2">
              {accomplishments.summary.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="flex items-start"
                >
                  <ChevronRight className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </motion.li>
              ))}
            </ul>
          </Card>

          {/* Projects */}
          <h3 className="text-2xl font-semibold mb-6">Detailed Project List</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accomplishments.projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6 h-full hover:border-blue-500 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <Folder className="w-8 h-8 text-blue-500" />
                    <Badge variant="secondary" className="bg-slate-700 text-xs">
                      {project.timeline}
                    </Badge>
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-white">{project.name}</h4>
                  <p className="text-gray-300 text-sm mb-3">{project.description}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.technologies.map((tech, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-blue-500 text-blue-400">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                  <div className="bg-slate-900 rounded p-3">
                    <p className="text-xs text-green-400 font-medium">{project.impact}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Excellence */}
      <section className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Shield className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Operational Excellence & Daily Impact</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {operationalExcellence.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6 h-full">
                  <h3 className="text-lg font-semibold mb-4 text-blue-400">{category.category}</h3>
                  <ul className="space-y-2">
                    {category.activities.map((activity, i) => (
                      <li key={i} className="flex items-start">
                        <ChevronRight className="w-4 h-4 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Training */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Book className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Complete Certification and Training History</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-blue-400">Certifications</h3>
              <div className="space-y-3">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Card className="bg-slate-800 border-slate-700 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-sm mb-1 text-white">{cert.name}</h4>
                          <p className="text-xs text-gray-400">{cert.issuer}</p>
                          <p className="text-xs text-gray-500 mt-1">{cert.date}</p>
                        </div>
                        <Badge className="bg-green-600">{cert.status}</Badge>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-blue-400">Professional Training</h3>
              <AccordionTraining training={training} />
            </div>
          </div>
        </div>
      </section>

      {/* Shout-outs */}
      <section className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <MessageSquare className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">Shout-Outs</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {shoutouts.map((shoutout, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800 border-slate-700 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center border-2 border-slate-600 overflow-hidden">
                      <img 
                        src={shoutout.badgeImage} 
                        alt={`Badge from ${shoutout.from}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-center mb-3">
                    <h4 className="font-semibold text-blue-400">{shoutout.from}</h4>
                    <p className="text-xs text-gray-500">{shoutout.date}</p>
                  </div>
                  <p className="text-gray-300 text-sm italic text-center">"{shoutout.message}"</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Review */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800 relative border-t border-slate-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-8">
            <Award className="w-8 h-8 text-blue-500 mr-3" />
            <h2 className="text-4xl font-bold">2025 Performance Review Highlights</h2>
          </div>
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-blue-500 p-8">
            <div className="text-center mb-6">
              <Badge className="bg-green-600 text-lg px-4 py-2">{performanceReview.rating}</Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-400">Key Highlights</h3>
                <ul className="space-y-2">
                  {performanceReview.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 text-blue-500 mr-2 mt-1 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-blue-400">Areas of Strength</h3>
                <div className="flex flex-wrap gap-2">
                  {performanceReview.areasOfStrength.map((strength, index) => (
                    <Badge key={index} variant="secondary" className="bg-blue-900 text-blue-200">
                      {strength}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-slate-900 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-3 text-blue-400">Manager Feedback</h3>
                <p className="text-gray-300 italic">"{performanceReview.managerQuote}"</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-blue-400">Peer Feedback</h3>
                <div className="space-y-4">
                  {performanceReview.peerFeedback.map((feedback, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      viewport={{ once: true }}
                      className={index !== performanceReview.peerFeedback.length - 1 ? "pb-4 border-b border-slate-700" : ""}
                    >
                      <p className="text-gray-300 italic leading-relaxed">"{feedback}"</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-4 bg-slate-800/80 relative border-t border-slate-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Let's Connect</h2>
          <p className="text-gray-300 text-lg mb-8">
            Interested in discussing cloud architecture, technical leadership, or potential opportunities?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Mail className="w-4 h-4 mr-2" />
              {personalInfo.email}
            </Button>
            <Button variant="outline" className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white">
              <Linkedin className="w-4 h-4 mr-2" />
              LinkedIn
            </Button>
            <Button variant="outline" className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white">
              <Github className="w-4 h-4 mr-2" />
              GitHub
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>&copy; 2025 {personalInfo.name}. All rights reserved.</p>
          <p className="text-sm mt-2">Professional Portfolio & Accomplishments Showcase</p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;